import PropTypes from 'prop-types';
import { FixedSizeList } from 'react-window';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { firstImage, ListingThumbnail } from 'src/components/listing-row';

import { plural, listingSummary } from './decisions';

// Smaller than a match's row: there is one picture here, not two, and nothing to compare.
const THUMBNAIL_SIZE = 40;
const ROW_HEIGHT = 64;

// About five rows before the list starts scrolling — enough to see it is a list without the section
// pushing the buttons off the screen.
const LIST_MAX_HEIGHT = 320;

/** One listing the seller can bring in, or not. */
function PickableRow ({ listing, picked, blocked, onToggle, divided }) {
  const title = listing?.title || 'Untitled item';
  const summary = listingSummary(listing);

  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      onClick={blocked ? undefined : onToggle}
      sx={{
        px: 1.5,
        height: 1,
        cursor: blocked ? 'default' : 'pointer',
        opacity: blocked ? 0.5 : 1,
        borderTop: divided ? (theme) => `1px solid ${theme.palette.divider}` : 'none',
        bgcolor: (theme) => (picked ? alpha(theme.palette.primary.main, 0.08) : 'transparent'),
        '&:hover': { bgcolor: (theme) => (blocked ? 'transparent' : theme.palette.action.hover) },
      }}
    >
      <Checkbox checked={picked} disabled={blocked} sx={{ p: 0.5, flexShrink: 0 }} />

      <ListingThumbnail
        src={firstImage(listing)}
        alt={title}
        size={THUMBNAIL_SIZE}
        rounded={1}
      />

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" noWrap title={title}>
          {title}
        </Typography>

        {summary ? (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            {summary}
          </Typography>
        ) : null}
      </Box>

      {listing?.isInactive ? (
        <Chip label="Draft" size="small" variant="soft" sx={{ height: 22, flexShrink: 0 }} />
      ) : null}
    </Stack>
  );
}

PickableRow.propTypes = {
  listing: PropTypes.object.isRequired,
  picked: PropTypes.bool,
  blocked: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  divided: PropTypes.bool,
};

/**
 * Choosing which of the rest of a catalogue to bring in, when it will not all fit the seller's plan.
 *
 * This only ever appears at a plan limit. Everywhere else these listings simply arrive, because
 * there is nothing to decide about them — so the whole section is a consequence of the limit, and it
 * says so rather than leaving the seller to work out why they are being asked.
 *
 * Nothing is pre-picked: choosing the first however-many on the seller's behalf would quietly decide
 * which of their own items get left behind. "Select N" makes taking that offer one press.
 */
export default function OtherListingsPicker ({
  rows,
  picked,
  onToggle,
  onSelectAsManyAsFit,
  onClear,
  fitCount,
  roomLeft,
  atCapacity,
}) {
  const anyPicked = picked.size > 0;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="subtitle2">
            Pick which of the other {rows.length} items to bring in
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Your plan has room for {plural(roomLeft, 'more listing')}. Drafts don&apos;t count.
          </Typography>
        </Box>

        <Button
          size="small"
          color="inherit"
          variant="outlined"
          sx={{ flexShrink: 0 }}
          onClick={anyPicked ? onClear : onSelectAsManyAsFit}
          disabled={!anyPicked && fitCount === 0}
        >
          {anyPicked ? 'Clear' : `Select ${fitCount}`}
        </Button>
      </Stack>

      <Card variant="outlined" sx={{ overflow: 'hidden' }}>
        <FixedSizeList
          height={Math.min(LIST_MAX_HEIGHT, rows.length * ROW_HEIGHT)}
          itemCount={rows.length}
          itemSize={ROW_HEIGHT}
          width="100%"
          overscanCount={4}
        >
          {({ index, style }) => {
            const { key, listing } = rows[index];
            const isPicked = picked.has(key);

            return (
              <div style={style}>
                <PickableRow
                  listing={listing}
                  picked={isPicked}
                  // A draft is free, so only a live listing can run out of room.
                  blocked={!isPicked && !listing.isInactive && atCapacity}
                  onToggle={() => onToggle(key)}
                  divided={index > 0}
                />
              </div>
            );
          }}
        </FixedSizeList>
      </Card>

      {atCapacity ? (
        <Typography variant="caption" color="text.secondary">
          That&apos;s all your plan has room for. Drafts can still be added.
        </Typography>
      ) : null}
    </Stack>
  );
}

OtherListingsPicker.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string.isRequired, listing: PropTypes.object.isRequired })
  ).isRequired,
  picked: PropTypes.instanceOf(Set).isRequired,
  onToggle: PropTypes.func.isRequired,
  onSelectAsManyAsFit: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  /** How many rows the "Select" button would pick — every draft, plus the live ones that fit. */
  fitCount: PropTypes.number.isRequired,
  /** Room left on the plan after everything the seller has decided so far. */
  roomLeft: PropTypes.number.isRequired,
  // True once what the seller has decided has used up the plan's room.
  atCapacity: PropTypes.bool,
};
